import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import type { EvidencePackRequest } from 'src/app/models/evidencePackRequest.model';

/**
 * A single selectable component in the evidence pack. `flag` is the {@link EvidencePackRequest} property it
 * toggles. When `disabled` is true the checkbox is pre-unchecked and locked (the user lacks the permission
 * named by `disabledReason`); the server-side gate is authoritative, this is UX only.
 */
export type EvidencePackComponentFlags = Pick<EvidencePackRequest,
    'includeChainOfCustody' | 'includeLabCertificates' | 'includeLetters' |
    'includeFieldSheets' | 'includeCalibrationRecords' | 'includeChangeHistory'>;

export interface EvidencePackComponentOption {
    flag: keyof EvidencePackComponentFlags;
    label: string;
    checked: boolean;
    disabled: boolean;
    disabledReason?: string;
}

export interface EvidencePackDialogData {
    components: EvidencePackComponentOption[];
}

@Component({
  standalone: false,
    selector: 'app-evidence-pack-dialog',
    templateUrl: './evidence-pack-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./evidence-pack-dialog.component.scss'],
})
export class EvidencePackDialogComponent {
    components: EvidencePackComponentOption[];
    public trackByIndex = trackByIndex;

    constructor(
        public dialogRef: MatDialogRef<EvidencePackDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: EvidencePackDialogData
    ) {
        this.components = (data.components ?? []).map(component => ({ ...component }));
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onExport(): void {
        const flags: EvidencePackComponentFlags = {
            includeChainOfCustody: false,
            includeLabCertificates: false,
            includeLetters: false,
            includeFieldSheets: false,
            includeCalibrationRecords: false,
            includeChangeHistory: false
        };

        for (const component of this.components) {
            flags[component.flag] = component.checked && !component.disabled;
        }

        this.dialogRef.close(flags);
    }
}
